import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DataTableComponent, TableColumn, PaginationEvent } from '@shared/components/data-table/data-table.component';
import { SearchFormComponent, SearchFormData } from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent, ActionButton } from '@shared/components/status-filter/status-filter.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OrderSourceModel } from './order-source.model';
import { OrderSourceService } from './order-source.service';

@Component({
  selector: 'app-order-source',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    TranslateModule
  ],
  templateUrl: './order-source.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderSourceComponent implements OnInit {
  private orderSourceService = inject(OrderSourceService);
  private translateService = inject(TranslateService);

  orders: OrderSourceModel[] = [];
  selectedOrders: OrderSourceModel[] = [];
  loading = false;
  totalRecords = 0;
  first = 0;
  rows = 20;

  // Status filter options
  statusOptions = [
    { key: 'all', label: 'order.source.status.all', active: true },
    { key: 'pending', label: 'order.source.status.pending_source', },
    { key: 'success', label: 'order.source.status.source_success', },
    { key: 'failed', label: 'order.source.status.source_failed', },
    { key: 'cancelled', label: 'order.source.status.source_cancelled', }
  ];
  activeStatus = 'all';

  // Dropdown options - mapping of values to translation keys
  private channelMap = {
    'Facebook': 'order.source.options.facebook',
    'Shopee': 'order.source.options.shopee',
    'Tmall': 'order.source.options.tmall'
  };
  private storeMap = {
    'Happy Selection Online Store': 'order.source.options.happy_selection_online_store',
    'Colorful Good Goods Warehouse': 'order.source.options.colorful_good_goods_warehouse',
    'Trend Station': 'order.source.options.trend_station'
  };
  private sourceStatusMap = {
    'Source Success': 'order.source.options.source_success',
    'Source Failed': 'order.source.options.source_failed',
    'Source Cancelled': 'order.source.options.source_cancelled',
    'Pending Source': 'order.source.options.pending_source'
  };

  channels = ['Facebook', 'Shopee', 'Tmall'];
  stores = ['Happy Selection Online Store', 'Colorful Good Goods Warehouse', 'Trend Station'];
  sourceStatuses = ['Source Success', 'Source Failed', 'Source Cancelled', 'Pending Source'];

  // Button configurations
  primaryButton: ActionButton = {
    label: 'order.source.actions.re_source',
    severity: 'info',
    outlined: false,
    disabled: false,
    show: true
  };

  secondaryButton: ActionButton = {
    label: 'order.source.actions.specify_shipment',
    severity: 'info',
    outlined: true,
    disabled: true,
    show: true
  };

  // Table columns
  columns: TableColumn[] = [
    { label: 'order.source.table.third_party_order_number', prop: 'thirdPartyOrderNumber', width: 150 },
    { label: 'order.source.table.internal_order_number', prop: 'internalOrderNumber', width: 150 },
    { label: 'order.source.table.source_order_number', prop: 'sourceOrderNumber', width: 150 },
    { label: 'order.source.table.product_line_count', prop: 'productLineCount', width: 120 },
    { label: 'order.source.table.source_status', prop: 'sourceStatus', width: 120 },
    { label: 'order.source.table.source_success', prop: 'sourceSuccess', width: 120 },
    { label: 'order.source.table.source_failed', prop: 'sourceFailed', width: 120 },
    { label: 'order.source.table.failure_reason', prop: 'failureReason', width: 150 },
    { label: 'order.source.table.sales_store', prop: 'salesStore', width: 120 },
    { label: 'order.source.table.sales_channel', prop: 'salesChannel', width: 120 },
    { label: 'order.source.table.third_party_order_time', prop: 'thirdPartyOrderTime', width: 150 },
    { label: 'order.source.table.payment_time', prop: 'paymentTime', width: 150 },
    { label: 'order.source.table.source_creation_time', prop: 'sourceCreationTime', width: 150 }
  ];

  ngOnInit(): void {
    this.loadOrders();
  }

  loadOrders(): void {
    this.loading = true;
    const searchParams = {
      status: this.activeStatus,
      first: this.first,
      rows: this.rows
    };

    this.orderSourceService.getOrders(searchParams).subscribe({
      next: (response) => {
        this.orders = response.data;
        this.totalRecords = response.total;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load orders:', error);
        this.loading = false;
      }
    });
  }

  onSearch(searchData: SearchFormData): void {
    this.first = 0;
    this.loadOrders();
  }

  onReset(): void {
    this.activeStatus = 'all';
    this.first = 0;
    this.loadOrders();
  }

  onStatusChange(status: string): void {
    this.activeStatus = status;
    this.first = 0;
    this.loadOrders();
  }

  onPageChange(event: PaginationEvent): void {
    this.first = event.first || 0;
    this.rows = event.rows || 20;
    this.loadOrders();
  }

  onSelectionChange(event: any): void {
    this.selectedOrders = event;
    // Enable/disable specify shipment button based on selection
    this.secondaryButton.disabled = this.selectedOrders.length === 0;
  }

  reSource(): void {
    if (this.selectedOrders.length === 0) {
      console.warn('Please select orders to re-source');
      return;
    }

    const orderIds = this.selectedOrders.map(order => order.id);
    this.orderSourceService.reSource(orderIds).subscribe({
      next: () => {
        console.log('Re-source initiated successfully');
        this.selectedOrders = [];
        this.loadOrders();
      },
      error: (error) => {
        console.error('Failed to re-source:', error);
      }
    });
  }

  specifyShipment(): void {
    if (this.selectedOrders.length === 0) {
      console.warn('Please select orders to specify shipment');
      return;
    }

    const orderIds = this.selectedOrders.map(order => order.id);
    this.orderSourceService.specifyShipment(orderIds).subscribe({
      next: () => {
        console.log('Shipment specification initiated successfully');
        this.selectedOrders = [];
        this.loadOrders();
      },
      error: (error) => {
        console.error('Failed to specify shipment:', error);
      }
    });
  }

  exportData(): void {
    this.orderSourceService.exportOrders({}).subscribe({
      next: (response) => {
        // Handle file download
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'order-source-data.csv';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Failed to export data:', error);
      }
    });
  }

  refreshData(): void {
    this.loadOrders();
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Source Success':
        return 'success';
      case 'Source Failed':
        return 'danger';
      case 'Source Cancelled':
        return 'warning';
      case 'Pending Source':
        return 'info';
      default:
        return 'info';
    }
  }

  // Dropdown option getters
  getChannelOptions() {
    return this.channels.map(s => ({
      label: this.translateService.instant(this.channelMap[s as keyof typeof this.channelMap]),
      value: s
    }));
  }

  getStoreOptions() {
    return this.stores.map(s => ({
      label: this.translateService.instant(this.storeMap[s as keyof typeof this.storeMap]),
      value: s
    }));
  }

  getSourceStatusOptions() {
    return this.sourceStatuses.map(s => ({
      label: this.translateService.instant(this.sourceStatusMap[s as keyof typeof this.sourceStatusMap]),
      value: s
    }));
  }
} 